<script setup lang="ts">
import TruncatableSpan from "@/components/ui/TruncatableSpan.vue";
import { useUsers } from "@/services/users";
import { hsbToHls, sha } from "@/services/utils";
import { onMounted, ref, watch } from "vue";

const {
  name,
  email = "",
  userId,
} = defineProps<{
  name: string;
  email?: string;
  userId?: string;
}>();

const users = useUsers();
const hasProfilePicture = ref(false);
const profilePictureUrl = ref("");
const light = ref("");
const dark = ref("");
const text = ref("");

async function computeColors() {
  const hash = await sha(name);
  const h = Math.abs(parseInt(hash, 16) % 318) + 42;
  const d = hsbToHls(h, 71, 100);
  const l = hsbToHls((h - 42) % 360, 29, 98);
  const t = hsbToHls(h, 71, 50);

  light.value = `hsl(${l.h}deg ${l.s}% ${l.l}%)`;
  dark.value = `hsl(${d.h}deg ${d.s}% ${d.l}%)`;
  text.value = `hsl(${t.h}deg ${t.s}% ${t.l}%)`;
}

async function fetchGravatar() {
  let emailToUse = email;
  if (emailToUse.length === 0 && userId) {
    const user = await users.getUser(userId);
    if (user) {
      emailToUse = user.email;
    }
  }
  if (emailToUse.length > 0) {
    const { url, hasProfilePicture: hasImage } = await users.getGravatarAvatarInfo(emailToUse);
    profilePictureUrl.value = url;
    hasProfilePicture.value = hasImage;
  }
}

watch(
  () => name,
  async () => {
    await computeColors();
  }
);
onMounted(async () => {
  await computeColors();
  await fetchGravatar();
});
</script>

<template>
  <div
    class="avatar"
    :style="{
      '--light': light,
      '--dark': dark,
      '--text': text,
    }"
  >
    <div class="profile-picture">
      <img v-if="hasProfilePicture" :src="profilePictureUrl" alt="profile picture" />
      <span v-else>
        {{ name[0] }}
      </span>
    </div>
    <span class="avatar-name">
      <TruncatableSpan :text="name" />
    </span>
  </div>
</template>

<style scoped>
.avatar {
  display: flex;
  overflow: hidden;
  flex-direction: row;
  align-items: center;
  color: var(--content-primary);
  gap: var(--numbers-8);
  text-overflow: ellipsis;
  white-space: nowrap;

  .profile-picture {
    display: flex;
    overflow: hidden;
    width: var(--numbers-24);
    height: var(--numbers-24);
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: var(--numbers-12);
    background: linear-gradient(to top, var(--dark), var(--light));
    color: var(--text);
    font-size: var(--numbers-12);
  }

  .avatar-name {
    min-width: 0;
    flex: 1;
  }
}
</style>
